// Media Session API helpers for the lock-screen / Now Playing widget.
// Wraps `navigator.mediaSession` so callers can treat unsupported browsers
// as no-ops without polluting component code.

const ACTIONS = [
  'play',
  'pause',
  'seekbackward',
  'seekforward',
  'seekto',
  'previoustrack',
  'nexttrack',
];

export function isMediaSessionSupported() {
  return typeof navigator !== 'undefined' && 'mediaSession' in navigator;
}

export function setMediaSessionPlaybackState(state) {
  if (!isMediaSessionSupported()) return;
  navigator.mediaSession.playbackState = state;
}

export function clearMediaSession() {
  if (!isMediaSessionSupported()) return;
  navigator.mediaSession.metadata = null;
  navigator.mediaSession.playbackState = 'none';
}

function setHandler(action, handler) {
  try {
    navigator.mediaSession.setActionHandler(action, handler);
  } catch (e) {
    // Some browsers throw on unsupported actions; ignore.
  }
}

// Registers metadata + action handlers for the currently playing chapter.
// Returns a cleanup function that unregisters all handlers.
//
// `getAudio` must return the current HTMLAudioElement (or null). Using a
// getter rather than a direct reference allows callers to pass a React ref
// without forcing the effect to re-run when the element identity changes.
export function setupMediaSession({
  metadata,
  getAudio,
  onPreviousTrack,
  onNextTrack,
} = {}) {
  if (!isMediaSessionSupported()) return () => {};

  navigator.mediaSession.metadata = new window.MediaMetadata(metadata || {});

  setHandler('play', () => {
    const audio = getAudio && getAudio();
    if (!audio) return;
    const p = audio.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  });

  setHandler('pause', () => {
    const audio = getAudio && getAudio();
    if (audio) audio.pause();
  });

  setHandler('seekbackward', (details) => {
    const audio = getAudio && getAudio();
    if (!audio) return;
    const offset = (details && details.seekOffset) || 30;
    audio.currentTime = Math.max(audio.currentTime - offset, 0);
  });

  setHandler('seekforward', (details) => {
    const audio = getAudio && getAudio();
    if (!audio) return;
    const offset = (details && details.seekOffset) || 30;
    const max = Number.isFinite(audio.duration)
      ? audio.duration
      : audio.currentTime + offset;
    audio.currentTime = Math.min(audio.currentTime + offset, max);
  });

  setHandler('seekto', (details) => {
    const audio = getAudio && getAudio();
    if (!audio || !details || details.seekTime == null) return;
    if (details.fastSeek && typeof audio.fastSeek === 'function') {
      audio.fastSeek(details.seekTime);
      return;
    }
    audio.currentTime = details.seekTime;
  });

  setHandler('previoustrack', () => {
    if (typeof onPreviousTrack === 'function') onPreviousTrack();
  });

  setHandler('nexttrack', () => {
    if (typeof onNextTrack === 'function') onNextTrack();
  });

  return () => {
    ACTIONS.forEach((action) => {
      try {
        navigator.mediaSession.setActionHandler(action, null);
      } catch (e) {
        // ignore
      }
    });
  };
}
